import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { BitrixAPI, CampaignAPI, NetworksAPI } from "../../services/api";
import { BitrixTask, Campaign, Network } from "../../types";
import { CAMPAIGN_TYPE_OPTIONS } from '../../utils/campaignTypes';

const STATUS_OPTIONS: { value: Campaign["status"]; label: string }[] = [
  { value: "planned", label: "Planned" },
  { value: "running", label: "Running" },
  { value: "finished", label: "Finished" },
];

interface CreateCampaignModalProps {
  show: boolean;
  onHide: () => void;
  /** Called with the freshly created campaign so the parent can refresh its list. */
  onCreated: (campaign: Campaign) => void;
}

interface NetworkOption {
  value: string; // network slug (the list endpoint does not include uuid)
  label: string;
  network: Network;
}

interface TaskOption {
  value: number; // bitrix task id
  label: string;
  task: BitrixTask;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({ show, onHide, onCreated }) => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const [networks, setNetworks] = useState<Network[]>([]);
  const [bitrixTasks, setBitrixTasks] = useState<BitrixTask[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // The campaign name and bitrix_id are both derived from the selected Bitrix task.
  const [selectedTask, setSelectedTask] = useState<TaskOption | null>(null);
  const [status, setStatus] = useState<Campaign["status"]>("running");
  const [type, setType] = useState<Campaign['type']>('BOTH');
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkOption | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Reset the form and load options each time the modal opens.
  useEffect(() => {
    if (!show) return;
    setSelectedTask(null);
    setStatus("running");
    setType('BOTH');
    setSelectedNetwork(null);
    setFormError(null);

    let cancelled = false;
    setLoadingOptions(true);
    Promise.all([NetworksAPI.getNetworks(), BitrixAPI.getTasks()])
      .then(([networksRes, tasksRes]) => {
        if (cancelled) return;
        setNetworks(networksRes.data);
        setBitrixTasks(tasksRes.data);
      })
      .catch((err) => {
        if (!cancelled) setError(`Failed to load campaign options: ${err}`);
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => { cancelled = true; };
  }, [show, setError]);

  const networkOptions = useMemo<NetworkOption[]>(
    () => networks.map((n) => ({
      value: n.slug,
      label: n.nameLong ? `${n.nameLong} (${n.slug})` : `${n.name} (${n.slug})`,
      network: n,
    })),
    [networks]
  );

  const taskOptions = useMemo<TaskOption[]>(
    () => bitrixTasks.map((t) => ({ value: t.id, label: `${t.name} (#${t.id})`, task: t })),
    [bitrixTasks]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedTask) {
      setFormError("Please select a Bitrix task.");
      return;
    }
    if (!selectedNetwork) {
      setFormError("Please select a network.");
      return;
    }

    setSubmitting(true);
    try {
      // The networks list endpoint doesn't include the uuid, so resolve it from
      // the detail endpoint (fall back to any uuid already present on the option).
      const networkSlug = selectedNetwork.network.slug;
      let networkUuid = selectedNetwork.network.uuid;
      if (!networkUuid) {
        const detail = await NetworksAPI.getNetworkDetail(networkSlug);
        networkUuid = detail.data.uuid;
      }
      if (!networkUuid) {
        setFormError("Could not resolve the network's uuid. Please try another network.");
        setSubmitting(false);
        return;
      }

      const response = await CampaignAPI.createCampaign({
        // Name and bitrix_id come straight from the chosen Bitrix task.
        // The CVAT project is created server-side from the configured template.
        name: selectedTask.task.name,
        bitrix_id: selectedTask.task.id,
        network_uuid: networkUuid,
        network_slug: networkSlug,
        status,
        type,
      });
      setSuccess(`Campaign "${response.data.name}" created (CVAT project #${response.data.cvat_project_id}).`);
      onCreated(response.data);
      onHide();
    } catch (err: unknown) {
      // Surface backend validation/CVAT errors (e.g. duplicate bitrix_id) inline.
      const axiosErr = err as { response?: { data?: Record<string, unknown> } };
      const data = axiosErr?.response?.data;
      let message = "Failed to create campaign.";
      if (data && typeof data === "object") {
        const parts = Object.entries(data).map(([field, val]) =>
          `${field}: ${Array.isArray(val) ? val.join(", ") : String(val)}`
        );
        if (parts.length) message = parts.join(" | ");
      }
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>New campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger" className="py-2">{formError}</Alert>}

          <Form.Group className="mb-3" controlId="campaign-task">
            <Form.Label>Bitrix task</Form.Label>
            <Select
              inputId="campaign-task"
              options={taskOptions}
              value={selectedTask}
              onChange={(opt) => setSelectedTask(opt as TaskOption | null)}
              isLoading={loadingOptions}
              isDisabled={loadingOptions}
              placeholder={loadingOptions ? "Loading Bitrix tasks..." : "Search or choose a task..."}
              noOptionsMessage={() => "No Bitrix tasks available"}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
            <Form.Text className="text-muted">
              The campaign name and Bitrix id are taken from the selected task. A CVAT
              project (cloned from the configured template) is created automatically.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="campaign-network">
            <Form.Label>Network</Form.Label>
            <Select
              inputId="campaign-network"
              options={networkOptions}
              value={selectedNetwork}
              onChange={(opt) => setSelectedNetwork(opt as NetworkOption | null)}
              isLoading={loadingOptions}
              isDisabled={loadingOptions}
              placeholder={loadingOptions ? "Loading networks..." : "Search or choose a network..."}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="new-campaign-type">
            <Form.Label>Campaign type</Form.Label>
            <Form.Select value={type} onChange={e => setType(e.target.value as Campaign['type'])}>
              {CAMPAIGN_TYPE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-1" controlId="campaign-status">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value as Campaign["status"])}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting || loadingOptions}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              "Create campaign"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateCampaignModal;
